/**
 * Il link di una GARA — torneo o arena — nelle due direzioni: come si scrive e
 * come si legge.
 *
 * Le due metà stanno nello stesso file apposta. Prima il costruttore viveva in
 * `utils/share.ts` e il lettore in `App.tsx`, a milletrecento righe di distanza,
 * e l'unica cosa che li teneva d'accordo era un commento su ciascuno dei due che
 * nominava l'altro. Un link che l'app non sa leggere non apre niente, e un
 * lettore che cerca una chiave che nessuno scrive non si accorge di niente:
 * nessuno dei due casi dà un errore. Qui la proprietà «quello che scriviamo è
 * esattamente quello che sappiamo leggere» si può fissare con un test, e il test
 * è in `gara-link-stabile.test.ts`.
 *
 * `tournament` e `arena` sono due chiavi distinte perché sono due schermate
 * distinte — il dettaglio dell'arena ha il boost, la pausa e il podio — e una
 * chiave sola obbligherebbe chi apre il link a interrogare il server per sapere
 * dove atterrare. Il gettone si chiama `t` in entrambi i casi: è la stessa
 * chiave, con lo stesso trattamento.
 */

export type GenereGara = 'tournament' | 'arena';

/**
 * Il PERCORSO di una gara: `/?tournament=<id>`. Relativo e senza origin, perché
 * è anche quello che `pushState` scrive nella barra degli indirizzi (vedi
 * `locationToUrl` in App.tsx) — l'origin lì sarebbe ridondante.
 *
 * MAI il gettone: questo percorso finisce nella barra, cioè sotto gli occhi di
 * chiunque guardi lo schermo e dentro qualunque copia-incolla dell'indirizzo. Il
 * gettone è la chiave d'ingresso di una gara privata e viaggia solo nel link
 * d'invito, che si manda a una persona alla volta.
 */
export function percorsoGara(genere: GenereGara, id: string): string {
  return `/?${genere}=${encodeURIComponent(id)}`;
}

/** Lo stesso percorso con l'origin davanti: quello che si manda in giro. */
export function garaUrl(genere: GenereGara, id: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${percorsoGara(genere, id)}`;
}

/** Quello che un link di gara porta: l'identificativo, e il gettone se c'è. */
export interface InvitoGara {
  id: string;
  /** Il gettone c'è solo per le gare private: per le pubbliche è `null`. */
  token: string | null;
}

/**
 * L'altra direzione. Un identificativo malformato non apre niente — `null` vuol
 * dire «questo link non è un invito», non «invito senza gettone».
 *
 * I due tetti (64 e 32 caratteri) sono gli stessi che il server accetta: un
 * valore più lungo non è un dato troncato da recuperare, è un link inventato.
 */
export function parseInvitoGara(params: URLSearchParams, genere: GenereGara): InvitoGara | null {
  const id = params.get(genere);
  if (!id || id.length > 64) return null;
  const token = params.get('t');
  return { id, token: token && token.length <= 32 ? token : null };
}

/** L'invito a un torneo: `?tournament=<id>[&t=<gettone>]`. */
export function parseTournamentDeepLink(params: URLSearchParams): InvitoGara | null {
  return parseInvitoGara(params, 'tournament');
}

/** L'invito a un'arena: `?arena=<id>[&t=<gettone>]`. */
export function parseArenaDeepLink(params: URLSearchParams): InvitoGara | null {
  return parseInvitoGara(params, 'arena');
}
